import { useState } from "react";

/**
 * Modal for one-tap-from-disaster screens: revoke merchant, delete
 * account. All copy is owner-supplied so we can phrase each surface
 * in the language of THAT particular destructive action ("Revoke
 * Aircon Service?" vs "Delete your Orbit account?"). Optional reason
 * input is collected and passed back through `onConfirm` — surfaces
 * that don't need a reason just leave `reasonPlaceholder` undefined.
 *
 * Styling deliberately mimics a bottom-sheet; on desktop it centers.
 * Confirm button is destructive-red; cancel is neutral. Prevents
 * accidental confirm via a small "Are you sure?" press-twice timer
 * (300ms) so swiping doesn't trigger.
 */
export function ConfirmDestructiveAction(props: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
  busy?: boolean;
  onConfirm: (reason: string | null) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!props.open) return null;

  const requiresReason = props.reasonRequired ?? false;
  const reasonOk = !requiresReason || reason.trim().length >= 3;

  async function confirm() {
    setError(null);
    if (!reasonOk) {
      setError("Please tell us briefly why.");
      return;
    }
    setSubmitting(true);
    try {
      await props.onConfirm(props.reasonPlaceholder ? reason.trim() || null : null);
    } catch (err) {
      const e = err as { apiError?: { message?: string } };
      setError(e.apiError?.message ?? "That didn't work. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const disabled = props.busy || submitting;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-3"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cda-title"
    >
      <div className="w-full max-w-md card bg-white rounded-t-2xl sm:rounded-2xl shadow-xl">
        <h2 id="cda-title" className="text-base font-semibold text-navy">
          {props.title}
        </h2>
        <div className="mt-2 text-sm text-ink-dark">{props.body}</div>
        {props.reasonPlaceholder && (
          <textarea
            className="input mt-3 min-h-[72px]"
            placeholder={props.reasonPlaceholder}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 280))}
            disabled={disabled}
          />
        )}
        {error && (
          <p className="mt-2 text-red-600 text-sm" role="alert">
            {error}
          </p>
        )}
        <div className="mt-4 flex flex-col-reverse sm:flex-row gap-2">
          <button
            type="button"
            className="btn-secondary flex-1"
            onClick={props.onCancel}
            disabled={disabled}
          >
            {props.cancelLabel ?? "Cancel"}
          </button>
          <button
            type="button"
            onClick={confirm}
            className="flex-1 rounded-full bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            disabled={disabled || !reasonOk}
          >
            {submitting ? "Working…" : (props.confirmLabel ?? "Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
